import { useState } from "react";
import { CiLocationOn } from "react-icons/ci";
import { ImConnection } from "react-icons/im";
import { FcHome } from "react-icons/fc";
import { getAccessToken } from "../../storage/storage";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import Swal from "sweetalert2";

export default function LodgifyImport() {
  const token = getAccessToken();
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [propertyMappings, setPropertyMappings] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const Navigate = useNavigate();
  const location = useLocation();

  const lodgifyData = location.state?.lodgifyData;

  const handlePropertySelect = (propertyId) => {
    setSelectedProperties(prev => {
      if (prev.includes(propertyId)) {
        return prev.filter(id => id !== propertyId);
      } else {
        return [...prev, propertyId];
      }
    });
  };

  const handleMappingChange = (lodgifyId, houseKeepingId) => {
    setPropertyMappings(prev => ({
      ...prev,
      [lodgifyId]: houseKeepingId === "" ? null : parseInt(houseKeepingId)
    }));
  };

  const handleSubmit = async () => {
    if (selectedProperties.length === 0) {
      setError("Please select at least one property");
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      const apiKey = location.state?.apiKey;
      if (!apiKey) {
        throw new Error("API key is missing");
      }

      // Create the properly formatted payload
      const payload = lodgifyData.lodgify_properties.items
        .filter(property => selectedProperties.includes(property.id))
        .map(property => {
          // Base property data
          const propertyData = {
            id: property.id,
            name: property.name,
            internal_name: property.internal_name || property.name,
            description: property.description || "",
            latitude: property.latitude || 0,
            longitude: property.longitude || 0,
            address: property.address || "",
            hide_address: property.hide_address || false,
            zip: property.zip || "",
            city: property.city || "",
            state: property.state || "",
            country_code: property.country_code || "",
            country: property.country || "",
            image_url: property.image_url || "",
            has_addons: property.has_addons || false,
            has_agreement: property.has_agreement || false,
            agreement_text: property.agreement_text || "",
            agreement_url: property.agreement_url || null,
            contact: {
              spoken_languages: property.contact?.spoken_languages || ["EN"]
            },
            rating: property.rating || 0,
            price_unit_in_days: property.price_unit_in_days || 1,
            min_price: property.min_price || 0,
            original_min_price: property.original_min_price || 0,
            max_price: property.max_price || 0,
            original_max_price: property.original_max_price || 0,
            rooms: property.rooms?.map(room => ({
              id: room.id,
              name: room.name
            })) || [],
            in_out_max_date: property.in_out_max_date || "0001-01-01",
            in_out: property.in_out || null,
            currency_code: property.currency_code || "USD",
            created_at: property.created_at || new Date().toISOString(),
            updated_at: property.updated_at || new Date().toISOString(),
            is_active: property.is_active !== undefined ? property.is_active : true,
            subscription_plans: property.subscription_plans || ["Professional"]
          };

          if (propertyMappings[property.id]) {
            propertyData.property_id = propertyMappings[property.id];
          }

          return propertyData;
        });

      console.log("Final Payload:", payload);

      const response = await axios.post(
        `${BASE_URL}/api/properties/sync_lodgify_properties/`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      Swal.fire({
        icon: "success",
        title: "Properties Mapped Successfully!",
        showConfirmButton: true,
        confirmButtonText: "Go to Properties",
        position: "center",
        width: "90%",
        padding: "1rem",
        customClass: {
          confirmButton: "swal-responsive-button",
          popup: "swal-responsive-popup"
        },
        buttonsStyling: false,
        allowOutsideClick: false,
        allowEscapeKey: false,
      }).then((result) => {
        if (result.isConfirmed) {
          Navigate("/settings/properties", { replace: true });
        }
      });

    } catch (err) {
      const errorMsg = err.response?.data?.error?.message ||
        err.response?.data?.message ||
        err.message ||
        "Import failed. Please try again.";

      setError(errorMsg);
      console.error("API Request Failed:", {
        error: err,
        response: err.response?.data,
        config: err.config
      });
    } finally {
      setIsSubmitting(false);
    }
  };



  return (
    <div className="max-w-2xl mx-auto space-y-6 my-10 bg-white shadow-lg p-6 rounded-lg">
      <h1 className="md:text-xl text-lg font-bold text-[#586989] mb-4">
        Map Your Properties
      </h1>
      <p className="text-gray-600 text-sm mb-6 text-start">
        Connect your HouseKeeper properties with their corresponding Lodgify properties to enable automatic booking synchronization.
      </p>

      <div className="border border-[#586989] rounded-md p-2 flex gap-2">
        <span className="ml-0 text-black mt-2"> <ImConnection /></span>
        <div className="text-sm text-black text-opacity-50">
          <strong>Smart Property Matching:</strong><span> We've automatically suggested property matches based on names, addresses and room
            configurations. Review and confirm each mapping below.</span>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <FcHome size={32} />
        <h1 className="md:text-xl text-lg font-bold text-[#586989]">
        Lodgify HK Properties
        </h1>
      </div>

      {lodgifyData?.lodgify_properties?.items?.length > 0 ? (
        <div className="space-y-4">
          {lodgifyData.lodgify_properties.items.map((property) => (
            <div
              key={property.id}
              className={`bg-white p-4 rounded-lg shadow border ${selectedProperties.includes(property.id) ? 'border-blue-500 bg-blue-50' : 'border-[#586989]'}`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-sm font-semibold">{property.name}</h2>
                  <div className="flex items-center gap-2">
                    <CiLocationOn />
                    <p className="text-sm ">{property.address}, {property.city}, {property.state}, {property.country}</p>
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={selectedProperties.includes(property.id)}
                  onChange={() => handlePropertySelect(property.id)}
                  className="h-5 w-5 text-blue-600 rounded"
                />
              </div>
              
              {/* Always show dropdown for each property */}
              <div className="mt-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Map to HouseKeeping Property:
                </label>
                <select
                  value={propertyMappings[property.id] || ""}
                  onChange={(e) => handleMappingChange(property.id, e.target.value)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                >
                  <option value="">Select a property</option>
                  {lodgifyData?.houseKeeping_properties?.map((hkProperty) => (
                    <option key={hkProperty.id} value={hkProperty.id}>
                      (ID: {hkProperty.id}) {hkProperty.name} 
                    </option>
                  ))}
                </select>
              </div>
            </div>
          ))}

          <button
            onClick={handleSubmit}
            disabled={isSubmitting || selectedProperties.length === 0}
            className="flex gap-2 bg-[#586989] text-white px-6 py-2 rounded-md disabled:opacity-50 mt-4"
          >
            {isSubmitting ? "Importing..." : `Import ${selectedProperties.length} Selected Properties`}
          </button>

          {error && <p className="text-red-500 mt-2">{error}</p>}
        </div>
      ) : (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p>No properties found in your Lodgify account.</p>
        </div>
      )}

      {/* Loading overlay */}
      {isSubmitting && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <p className="text-lg font-semibold">Importing properties...</p>
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mt-4"></div>
          </div>
        </div>
      )}
    </div>
  );
}